import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Cleave from "cleave.js/react";
import { toast } from "react-toastify";
import {
  Alert,
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";

import { tokenizeCreditCard } from "../../../helpers/asaasEncryption";
import {
  AvailablePlan,
  checkout,
  CouponPreview,
  CreditCardHolderInfo,
  MyTenantSubscription,
  SubscriptionCheckoutPix,
  SubscriptionPaymentMethod,
  syncSubscriptionPaymentStatus,
  validateSubscriptionCoupon,
} from "../../../services/subscriptionService";

const PIX_POLL_INTERVAL_MS = 5_000;
const PIX_POLL_MAX_ATTEMPTS = 24;

const COUPON_ERROR_CODES = [
  "COUPON_INVALID",
  "COUPON_EXPIRED",
  "COUPON_EXHAUSTED",
  "COUPON_PLAN_INELIGIBLE",
  "COUPON_CURRENCY_MISMATCH",
  "COUPON_ALREADY_APPLIED",
  "COUPON_UPGRADE_NOT_ALLOWED",
] as const;

type CouponErrorCode = (typeof COUPON_ERROR_CODES)[number];

const resolveIntlLocale = (language: string): string =>
  language === "sp" ? "es" : language;

const formatMoney = (cents: number, currency: string, language: string): string =>
  new Intl.NumberFormat(resolveIntlLocale(language), {
    style: "currency",
    currency,
  }).format(cents / 100);

const formatPeriodEnd = (iso: string, language: string): string =>
  new Intl.DateTimeFormat(resolveIntlLocale(language), {
    dateStyle: "long",
  }).format(new Date(iso));

const resolveCouponErrorMessage = (
  t: (key: string, options?: Record<string, unknown>) => string,
  error: { data?: string; code?: string } | null | undefined
): string => {
  const code = error?.code;

  if (code && COUPON_ERROR_CODES.includes(code as CouponErrorCode)) {
    return t(`settings.subscription.checkout.coupon.errors.${code}`);
  }

  return error?.data || t("settings.subscription.checkout.coupon.error");
};

const parsePixExpiresAt = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const withZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized)
    ? normalized
    : `${normalized}-03:00`;
  const date = new Date(withZone);

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatCountdown = (totalSeconds: number): string => {
  const safe = Math.max(0, totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

interface CheckoutModalProps {
  isOpen: boolean;
  plan: AvailablePlan | null;
  subscription: MyTenantSubscription | null;
  initialPix?: SubscriptionCheckoutPix | null;
  onClose: () => void;
  onSuccess: (
    subscription: MyTenantSubscription,
    message?: string,
    options?: { keepOpenForPix?: boolean; checkoutUrl?: string }
  ) => void;
  onPaymentConfirmed?: (subscription: MyTenantSubscription) => void;
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  plan,
  subscription,
  initialPix = null,
  onClose,
  onSuccess,
  onPaymentConfirmed,
}) => {
  const { t, i18n } = useTranslation();
  const [paymentTab, setPaymentTab] = useState<SubscriptionPaymentMethod>("credit_card");
  const [couponCode, setCouponCode] = useState("");
  const [couponPreview, setCouponPreview] = useState<CouponPreview | null>(null);
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [pixPayment, setPixPayment] = useState<SubscriptionCheckoutPix | null>(null);
  const [pixCopied, setPixCopied] = useState(false);
  const [pixRefreshing, setPixRefreshing] = useState(false);
  const [pixPolling, setPixPolling] = useState(false);
  const [pixConfirmed, setPixConfirmed] = useState(false);
  const [countdownLabel, setCountdownLabel] = useState<string | null>(null);
  const [pixExpired, setPixExpired] = useState(false);
  const pixConfirmedRef = useRef(false);

  const [cardNumber, setCardNumber] = useState("");
  const [cardName, setCardName] = useState("");
  const [cardCpf, setCardCpf] = useState("");
  const [pixCpf, setPixCpf] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const previousPlanIdRef = useRef<number | null>(null);

  const isPaidPlan = plan ? plan.base_price > 0 : false;
  const isUsdPaddlePlan = plan?.currency === "USD";

  useEffect(() => {
    if (!plan) {
      previousPlanIdRef.current = null;
      return;
    }

    if (
      previousPlanIdRef.current !== null &&
      previousPlanIdRef.current !== plan.id
    ) {
      setCouponCode("");
      setCouponPreview(null);
      setCouponError("");
      setPixPayment(null);
    }

    previousPlanIdRef.current = plan.id;
  }, [plan]);

  useEffect(() => {
    if (!isOpen) {
      setPixPayment(null);
      setPixCopied(false);
      setPixRefreshing(false);
      setPixPolling(false);
      setPixConfirmed(false);
      setCountdownLabel(null);
      setPixExpired(false);
      pixConfirmedRef.current = false;
      return;
    }

    if (initialPix?.copy_paste_string) {
      setPixPayment(initialPix);
    }

    const savedDocument = subscription?.billing_document?.replace(/\D/g, "") ?? "";

    if (savedDocument.length === 11 || savedDocument.length === 14) {
      setPixCpf(savedDocument);
      setCardCpf(savedDocument);
    }
  }, [isOpen, subscription?.billing_document, initialPix]);

  useEffect(() => {
    if (!pixPayment?.expires_at) {
      setCountdownLabel(null);
      setPixExpired(false);
      return undefined;
    }

    const expiresAt = parsePixExpiresAt(pixPayment.expires_at);

    if (!expiresAt) {
      setCountdownLabel(null);
      return undefined;
    }

    const tick = (): void => {
      const remaining = Math.floor((expiresAt.getTime() - Date.now()) / 1000);
      setPixExpired(remaining <= 0);
      setCountdownLabel(formatCountdown(remaining));
    };

    tick();
    const timerId = window.setInterval(tick, 1000);

    return () => window.clearInterval(timerId);
  }, [pixPayment?.expires_at]);

  useEffect(() => {
    if (!isOpen || !pixPayment?.copy_paste_string || pixConfirmedRef.current) {
      return undefined;
    }

    let attempts = 0;
    let stopped = false;
    setPixPolling(true);

    const poll = async (): Promise<void> => {
      if (stopped || pixConfirmedRef.current) {
        return;
      }

      attempts += 1;

      try {
        const response = await syncSubscriptionPaymentStatus();

        if (response.data.status === "active") {
          pixConfirmedRef.current = true;
          setPixConfirmed(true);
          setPixPolling(false);
          toast.success(
            response.message || t("settings.subscription.paymentPending.confirmed")
          );
          onPaymentConfirmed?.(response.data);
          return;
        }
      } catch {
        // silencioso — botão manual cobre falhas pontuais
      }

      if (attempts >= PIX_POLL_MAX_ATTEMPTS) {
        stopped = true;
        setPixPolling(false);
      }
    };

    void poll();
    const intervalId = window.setInterval(() => {
      void poll();
    }, PIX_POLL_INTERVAL_MS);

    return () => {
      stopped = true;
      window.clearInterval(intervalId);
      setPixPolling(false);
    };
  }, [isOpen, pixPayment?.copy_paste_string, onPaymentConfirmed, t]);

  const previewAmounts = useMemo(() => {
    if (!plan) {
      return null;
    }

    const original = plan.base_price;
    const finalCents = couponPreview?.preview_first_invoice_cents ?? original;
    const discount = Math.max(0, original - finalCents);

    return { original, discount, finalCents };
  }, [plan, couponPreview]);

  const firstChargeDate = subscription?.current_period_end
    ? formatPeriodEnd(subscription.current_period_end, i18n.language)
    : formatPeriodEnd(new Date(Date.now() + 30 * 86400000).toISOString(), i18n.language);

  const resetState = (): void => {
    setPaymentTab("credit_card");
    setCouponCode("");
    setCouponPreview(null);
    setCouponError("");
    setFormError("");
    setCardNumber("");
    setCardName("");
    setCardCpf("");
    setPixCpf("");
    setCardExpiry("");
    setCardCvv("");
    setPixPayment(null);
    setPixCopied(false);
    setPixRefreshing(false);
    setPixPolling(false);
    setPixConfirmed(false);
    setCountdownLabel(null);
    setPixExpired(false);
    pixConfirmedRef.current = false;
  };

  const handleCopyPixPayload = async (): Promise<void> => {
    if (!pixPayment?.copy_paste_string) {
      return;
    }

    try {
      await navigator.clipboard.writeText(pixPayment.copy_paste_string);
      setPixCopied(true);
      toast.success(t("settings.subscription.checkout.payment.pixCopied"));
    } catch {
      toast.error(t("settings.subscription.checkout.payment.pixCopyError"));
    }
  };

  const handleSharePixWhatsapp = (): void => {
    if (!pixPayment?.copy_paste_string) {
      return;
    }

    const planName = plan?.name ?? subscription?.plan?.name ?? "Adatis";
    const text = t("settings.subscription.checkout.payment.pixWhatsappText", {
      plan: planName,
      code: pixPayment.copy_paste_string,
    });
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  const handlePrintPix = (): void => {
    if (!pixPayment?.copy_paste_string) {
      return;
    }

    const planName = plan?.name ?? subscription?.plan?.name ?? "Adatis";
    const title = escapeHtml(
      t("settings.subscription.checkout.payment.pixPrintTitle", { plan: planName })
    );
    const copyLabel = escapeHtml(t("settings.subscription.checkout.payment.pixCopyLabel"));
    const copyPaste = escapeHtml(pixPayment.copy_paste_string);
    const safeBase64 = pixPayment.qr_code_base64?.replace(/[^A-Za-z0-9+/=]/g, "") ?? "";
    const qrHtml = safeBase64
      ? `<img src="data:image/png;base64,${safeBase64}" alt="PIX" width="240" height="240" />`
      : "";
    const printWindow = window.open("", "_blank", "noopener,noreferrer,width=480,height=720");

    if (!printWindow) {
      toast.error(t("settings.subscription.checkout.payment.pixPrintError"));
      return;
    }

    printWindow.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
<style>
body{font-family:Arial,sans-serif;color:#111;padding:24px;text-align:center}
code{display:block;word-break:break-all;background:#f3f4f6;padding:12px;border-radius:8px;text-align:left;margin-top:16px}
</style></head><body>
<h1>${title}</h1>
${qrHtml}
<p>${copyLabel}</p>
<code>${copyPaste}</code>
<script>window.onload=function(){window.print();}</script>
</body></html>`);
    printWindow.document.close();
  };

  const handleRefreshPixStatus = async (): Promise<void> => {
    setPixRefreshing(true);

    try {
      const response = await syncSubscriptionPaymentStatus();

      if (response.data.status === "active") {
        pixConfirmedRef.current = true;
        setPixConfirmed(true);
        setPixPolling(false);
        toast.success(
          response.message || t("settings.subscription.paymentPending.confirmed")
        );
        onPaymentConfirmed?.(response.data);
        return;
      }

      toast.info(
        response.message || t("settings.subscription.checkout.payment.pixStillPending")
      );
    } catch (error: any) {
      toast.error(error?.data || t("settings.subscription.toast.loadError"));
    } finally {
      setPixRefreshing(false);
    }
  };

  const handleClose = (): void => {
    if (submitting) {
      return;
    }

    resetState();
    onClose();
  };

  const handleApplyCoupon = async (): Promise<void> => {
    if (!plan || couponCode.trim() === "") {
      setCouponError(t("settings.subscription.checkout.coupon.required"));
      return;
    }

    setCouponLoading(true);
    setCouponError("");
    setCouponPreview(null);

    try {
      const response = await validateSubscriptionCoupon(plan.id, couponCode.trim());
      setCouponPreview(response.data);
    } catch (error: any) {
      setCouponError(resolveCouponErrorMessage(t, error));
    } finally {
      setCouponLoading(false);
    }
  };

  const buildHolderInfo = (): CreditCardHolderInfo | undefined => {
    const holder: CreditCardHolderInfo = {};

    if (cardName.trim()) {
      holder.name = cardName.trim();
    }

    if (cardCpf.trim()) {
      holder.cpfCnpj = cardCpf.replace(/\D/g, "");
    }

    return Object.keys(holder).length > 0 ? holder : undefined;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!plan) {
      return;
    }

    setFormError("");

    if (isPaidPlan && !isUsdPaddlePlan && paymentTab === "credit_card") {
      const digits = cardNumber.replace(/\D/g, "");

      if (
        digits.length < 13 ||
        !cardName.trim() ||
        cardExpiry.trim() === "" ||
        cardCvv.trim().length < 3
      ) {
        setFormError(t("settings.subscription.checkout.card.invalid"));
        return;
      }
    }

    if (isPaidPlan && !isUsdPaddlePlan && paymentTab === "pix") {
      const docDigits = pixCpf.replace(/\D/g, "");

      if (docDigits.length !== 11 && docDigits.length !== 14) {
        setFormError(t("settings.subscription.checkout.payment.pixDocumentInvalid"));
        return;
      }
    }

    setSubmitting(true);

    try {
      const payload: Parameters<typeof checkout>[0] = {
        plan_id: plan.id,
        ...(couponPreview?.coupon_code || couponCode.trim()
          ? { coupon_code: couponPreview?.coupon_code ?? couponCode.trim() }
          : {}),
      };

      if (isPaidPlan) {
        if (isUsdPaddlePlan) {
          payload.payment_method = "paddle";
        } else {
          payload.payment_method = paymentTab;

          if (paymentTab === "credit_card") {
            const tokenized = await tokenizeCreditCard({
              number: cardNumber,
              holderName: cardName,
              expiry: cardExpiry,
              ccv: cardCvv,
              holderInfo: buildHolderInfo(),
            });

            payload.credit_card_token = tokenized.creditCardToken;
            payload.credit_card_last4 = tokenized.creditCardLast4;
            payload.credit_card_holder_info = buildHolderInfo();
          }

          if (paymentTab === "pix") {
            payload.payer_document = pixCpf.replace(/\D/g, "");
          }
        }
      }

      const response = await checkout(payload);

      if (response.checkout_url) {
        onSuccess(response.data, response.message, {
          checkoutUrl: response.checkout_url,
        });
        resetState();
        return;
      }

      if (!isUsdPaddlePlan && paymentTab === "pix") {
        if (response.pix?.copy_paste_string) {
          setPixPayment(response.pix);
          onSuccess(response.data, response.message, { keepOpenForPix: true });
          return;
        }

        setFormError(t("settings.subscription.checkout.payment.pixMissing"));
        onSuccess(response.data, response.message, { keepOpenForPix: true });
        return;
      }

      onSuccess(response.data, response.message);
      resetState();
    } catch (error: any) {
      if (error?.message === "INVALID_EXPIRY") {
        setFormError(t("settings.subscription.checkout.card.invalid"));
      } else {
        setFormError(error?.data || t("settings.subscription.upgrade.toast.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!plan) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        {t("settings.subscription.checkout.title", { plan: plan.name })}
      </ModalHeader>
      <ModalBody>
        {formError ? <Alert color="danger">{formError}</Alert> : null}

        {pixPayment ? (
          <div className="text-center">
            {pixConfirmed ? (
              <Alert color="success" className="text-start">
                {t("settings.subscription.paymentPending.confirmed")}
              </Alert>
            ) : (
              <Alert color={pixExpired ? "warning" : "success"} className="text-start">
                {pixExpired
                  ? t("settings.subscription.checkout.payment.pixExpired")
                  : t("settings.subscription.checkout.payment.pixReady")}
              </Alert>
            )}
            {countdownLabel && !pixConfirmed ? (
              <p className={`small mb-2 ${pixExpired ? "text-danger" : "text-muted"}`}>
                {pixExpired
                  ? t("settings.subscription.checkout.payment.pixExpiredHint")
                  : t("settings.subscription.checkout.payment.pixCountdown", {
                      time: countdownLabel,
                    })}
              </p>
            ) : null}
            {pixPolling && !pixConfirmed ? (
              <p className="text-muted small mb-2">
                <Spinner size="sm" className="me-1" />
                {t("settings.subscription.checkout.payment.pixPolling")}
              </p>
            ) : null}
            {pixPayment.qr_code_base64 ? (
              <img
                src={`data:image/png;base64,${pixPayment.qr_code_base64}`}
                alt={t("settings.subscription.checkout.payment.pixQrAlt")}
                width={220}
                height={220}
                className="border rounded mb-3"
              />
            ) : null}
            <Label className="form-label d-block text-start">
              {t("settings.subscription.checkout.payment.pixCopyLabel")}
            </Label>
            <Input
              type="textarea"
              rows={4}
              value={pixPayment.copy_paste_string}
              readOnly
              className="font-monospace small mb-2"
            />
            <div className="d-flex flex-wrap justify-content-end gap-2">
              <Button color="light" onClick={handleClose}>
                {t("settings.subscription.checkout.actions.close")}
              </Button>
              <Button color="light" outline onClick={handlePrintPix}>
                <i className="ri-printer-line me-1" aria-hidden="true" />
                {t("settings.subscription.checkout.payment.pixPrint")}
              </Button>
              <Button color="success" outline onClick={handleSharePixWhatsapp}>
                <i className="ri-whatsapp-line me-1" aria-hidden="true" />
                {t("settings.subscription.checkout.payment.pixWhatsapp")}
              </Button>
              <Button color="primary" outline onClick={handleCopyPixPayload}>
                {pixCopied
                  ? t("settings.subscription.checkout.payment.pixCopiedButton")
                  : t("settings.subscription.checkout.payment.pixCopyButton")}
              </Button>
              {!pixConfirmed ? (
                <Button
                  color="primary"
                  onClick={() => void handleRefreshPixStatus()}
                  disabled={pixRefreshing}
                >
                  {pixRefreshing ? (
                    <Spinner size="sm" />
                  ) : (
                    t("settings.subscription.checkout.payment.pixPaidRefresh")
                  )}
                </Button>
              ) : null}
            </div>
            <p className="text-muted small text-start mt-3 mb-0">
              {t("settings.subscription.checkout.payment.pixEmailHint")}
            </p>
          </div>
        ) : null}

        {!pixPayment ? (
        <>
        <div className="mb-4">
          <Label className="form-label">{t("settings.subscription.checkout.coupon.label")}</Label>
          <div className="d-flex gap-2">
            <Input
              value={couponCode}
              onChange={(event) => {
                setCouponCode(event.target.value.toUpperCase());
                if (couponError) {
                  setCouponError("");
                }
              }}
              placeholder={t("settings.subscription.checkout.coupon.placeholder")}
              disabled={submitting || couponLoading}
              invalid={Boolean(couponError)}
            />
            <Button
              color="soft-primary"
              outline
              disabled={submitting || couponLoading || couponCode.trim() === ""}
              onClick={handleApplyCoupon}
            >
              {couponLoading ? <Spinner size="sm" /> : t("settings.subscription.checkout.coupon.apply")}
            </Button>
          </div>
          {couponError ? (
            <div className="text-danger small mt-1" role="alert">
              {couponError}
            </div>
          ) : null}
          {couponPreview ? (
            <Alert color="success" className="mt-2 mb-0 py-2">
              {t("settings.subscription.checkout.coupon.applied", {
                code: couponPreview.coupon_code,
              })}
            </Alert>
          ) : null}
        </div>

        {previewAmounts ? (
          <div className="border rounded p-3 mb-4 bg-light-subtle">
            <h6 className="mb-3">{t("settings.subscription.checkout.preview.title")}</h6>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">{t("settings.subscription.checkout.preview.original")}</span>
              <span>{formatMoney(previewAmounts.original, plan.currency, i18n.language)}</span>
            </div>
            {previewAmounts.discount > 0 ? (
              <div className="d-flex justify-content-between mb-2 text-success">
                <span>{t("settings.subscription.checkout.preview.discount")}</span>
                <span>
                  -{formatMoney(previewAmounts.discount, plan.currency, i18n.language)}
                </span>
              </div>
            ) : null}
            <div className="d-flex justify-content-between fw-semibold fs-5 mb-2">
              <span>{t("settings.subscription.checkout.preview.final")}</span>
              <span>{formatMoney(previewAmounts.finalCents, plan.currency, i18n.language)}</span>
            </div>
            <div className="text-muted small">
              {t("settings.subscription.checkout.preview.firstCharge", { date: firstChargeDate })}
            </div>
          </div>
        ) : null}

        {isPaidPlan && isUsdPaddlePlan ? (
          <Alert color="info" className="mb-0">
            {t("settings.subscription.checkout.paddle.hint")}
          </Alert>
        ) : null}

        {isPaidPlan && !isUsdPaddlePlan ? (
          <>
            <Nav tabs className="nav-tabs-custom nav-success mb-3">
              <NavItem>
                <NavLink
                  className={classnames({ active: paymentTab === "credit_card" })}
                  onClick={() => setPaymentTab("credit_card")}
                  style={{ cursor: "pointer" }}
                >
                  {t("settings.subscription.checkout.payment.creditCard")}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: paymentTab === "pix" })}
                  onClick={() => setPaymentTab("pix")}
                  style={{ cursor: "pointer" }}
                >
                  {t("settings.subscription.checkout.payment.pix")}
                </NavLink>
              </NavItem>
            </Nav>
            <TabContent activeTab={paymentTab}>
              <TabPane tabId="credit_card">
                <Row className="g-3">
                  <Col md={12}>
                    <Label>{t("settings.subscription.checkout.card.number")}</Label>
                    <Cleave
                      className="form-control"
                      options={{ creditCard: true }}
                      value={cardNumber}
                      onChange={(event: React.ChangeEvent<HTMLInputElement> & { target: { rawValue: string } }) =>
                        setCardNumber(event.target.rawValue)
                      }
                      placeholder={t("settings.subscription.checkout.card.numberPlaceholder")}
                      disabled={submitting}
                      autoComplete="cc-number"
                    />
                  </Col>
                  <Col md={12}>
                    <Label>{t("settings.subscription.checkout.card.name")}</Label>
                    <Input
                      value={cardName}
                      onChange={(event) => setCardName(event.target.value)}
                      disabled={submitting}
                      autoComplete="cc-name"
                    />
                  </Col>
                  <Col md={4}>
                    <Label>{t("settings.subscription.checkout.card.expiry")}</Label>
                    <Cleave
                      className="form-control"
                      options={{
                        date: true,
                        datePattern: ["m", "y"],
                      }}
                      value={cardExpiry}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setCardExpiry(event.target.value)
                      }
                      placeholder={t("settings.subscription.checkout.card.expiryPlaceholder")}
                      disabled={submitting}
                      autoComplete="cc-exp"
                    />
                  </Col>
                  <Col md={4}>
                    <Label>{t("settings.subscription.checkout.card.cvv")}</Label>
                    <Cleave
                      className="form-control"
                      options={{
                        blocks: [4],
                        numericOnly: true,
                      }}
                      value={cardCvv}
                      onChange={(event: React.ChangeEvent<HTMLInputElement> & { target: { rawValue: string } }) =>
                        setCardCvv(event.target.rawValue)
                      }
                      placeholder={t("settings.subscription.checkout.card.cvvPlaceholder")}
                      type="password"
                      disabled={submitting}
                      autoComplete="cc-csc"
                    />
                  </Col>
                  <Col md={4}>
                    <Label>{t("settings.subscription.checkout.card.cpf")}</Label>
                    <Input
                      value={cardCpf}
                      onChange={(event) => setCardCpf(event.target.value)}
                      disabled={submitting}
                    />
                  </Col>
                </Row>
                <p className="text-muted small mt-2 mb-0">
                  {t("settings.subscription.checkout.card.tokenHint")}
                </p>
              </TabPane>
              <TabPane tabId="pix">
                <Alert color="info" className="mb-3">
                  {t("settings.subscription.checkout.payment.pixHint")}
                </Alert>
                <Label htmlFor="subscription-pix-cpf">
                  {t("settings.subscription.checkout.payment.pixDocument")}
                </Label>
                <Input
                  id="subscription-pix-cpf"
                  value={pixCpf}
                  onChange={(event) => setPixCpf(event.target.value)}
                  placeholder={t("settings.subscription.checkout.payment.pixDocumentPlaceholder")}
                  disabled={submitting}
                  inputMode="numeric"
                  autoComplete="off"
                />
                <p className="text-muted small mt-2 mb-0">
                  {t("settings.subscription.checkout.payment.pixDocumentHint")}
                </p>
              </TabPane>
            </TabContent>
          </>
        ) : null}

        {!isPaidPlan ? (
          <Alert color="success">{t("settings.subscription.checkout.freePlanHint")}</Alert>
        ) : null}

        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button color="light" onClick={handleClose} disabled={submitting}>
            {t("settings.subscription.checkout.actions.cancel")}
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t("settings.subscription.upgrade.processing")}
              </>
            ) : isUsdPaddlePlan ? (
              t("settings.subscription.checkout.paddle.continue")
            ) : (
              t("settings.subscription.checkout.actions.confirm")
            )}
          </Button>
        </div>
        </>
        ) : null}
      </ModalBody>
    </Modal>
  );
};

export default CheckoutModal;
